import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Spin, Row, Col, Button, Tabs, Form } from 'antd';
import { Prompt, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	GalleryItemGroup,
	GalleryItemType,
	ProductGalleryItemDto,
	ProjectProductDetailsDto,
	UpdateProjectStoreDetailsCommand,
} from 'api';
import { getGalleryItems } from 'utils/productUtils';
import OmniNotification from 'components/OmniNotification';
import * as yup from 'yup';
import { getProjectProduct, updateProjectStoreDetails } from 'redux/reducers/projectsSlice';
import { Controller, FieldValues, FormProvider, Path, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	FileModel,
	FileYupSchema,
	generateUploadFileList,
	GetUploadLink,
	ImageFileModel,
	ValidateS3Keys,
	isVideoExactResolutionValid,
	isFileFormatValid,
} from 'utils/fileUtils';
import ImageFileUpload from 'components/ImageFileUpload';
import FileUpload from '../../components/FileUpload';
import { focusFirstErrorField } from '../../utils/validationUtils';
import { min, isEmpty } from 'lodash';
import RouteLeavingGuard from 'components/RouteLeavingGuard';
import { confirmResultType } from 'components/RouteLeavingGuard/RouteLeavingGuard';

type ParamsProps = {
	projectId: string;
};

const SCREENSHOTS_LENGTH = 6;
const SCREENSHOTS_REQUIRED_LENGTH = 2;
const VIDEO_EXACT_RESOLUTION = {
	width: 1280,
	height: 720,
};
const VIDEO_ALLOWED_FORMATS = ['.mp4'];

interface FormValues extends FieldValues {
	landscapeImage: ImageFileModel | null;
	coverImage: ImageFileModel | null;
	heroImage: ImageFileModel | null;
	screenshot1: ImageFileModel | null;
	screenshot2: ImageFileModel | null;
	screenshot3?: ImageFileModel | null | undefined;
	screenshot4?: ImageFileModel | null | undefined;
	screenshot5?: ImageFileModel | null | undefined;
	screenshot6?: ImageFileModel | null | undefined;
	video1: FileModel[];
	video1Image: ImageFileModel | null;
	video2: FileModel[];
	video2Image: ImageFileModel | null;
}
const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		landscapeImage: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.Landscapecoverimage'),
				}),
			),
		coverImage: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.Squarecoverimage'),
				}),
			),
		heroImage: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.Bannerheroimage'),
				}),
			),
		screenshot1: yup
			.object(FileYupSchema)
			.nullable()
			.test(
				'has-screenshot1',
				t('validation.required', {
					fieldName: t('addProductPage.Screenshot1'),
				}),
				(value, context) => {
					return hasEnoughImages(context.from[1].value);
				},
			),

		screenshot2: yup
			.object(FileYupSchema)
			.nullable()
			.test(
				'has-screenshot2',
				t('validation.required', {
					fieldName: t('addProductPage.Screenshot2'),
				}),
				(value, context) => {
					return hasEnoughImages(context.from[1].value);
				},
			),
		video1: yup
			.array()
			.of(yup.object(FileYupSchema))
			.test('ValidateS3Keys', '', ValidateS3Keys)
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.Video1'),
				}),
			)
			.min(
				1,
				t('validation.required', {
					fieldName: t('addProductPage.Video1'),
				}),
			),
		video1Image: yup
			.object(FileYupSchema)
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.Video1image'),
				}),
			),
		video2: yup.array().of(yup.object(FileYupSchema)).test('ValidateS3Keys', '', ValidateS3Keys),
		video2Image: yup.object(FileYupSchema).nullable(),
	});
const hasEnoughImages = (photos: any) => {
	let imageCount = 0;
	for (var i = 1; i < 7; i++) {
		if (photos['screenshot' + i] != null) {
			imageCount++;
		}
		if (imageCount >= 2) {
			return true;
		}
	}
	return false;
};
const defaultValues: FormValues = {
	landscapeImage: null,
	coverImage: null,
	heroImage: null,
	screenshot1: null,
	screenshot2: null,
	screenshot3: null,
	screenshot4: null,
	screenshot5: null,
	screenshot6: null,
	video1: [],
	video1Image: null,
	video2: [],
	video2Image: null,
};
export default function AssetsForm() {
	const { t } = useTranslation();
	const { projectId } = useParams<ParamsProps>();
	const validationSchema = getValidationSchema(t);
	const dispatch: AppDispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [productDetails, setProductDetails] = useState<ProjectProductDetailsDto>();
	const [shouldCallSubmitWhenNavigatingAway, setShouldCallSubmitWhenNavigatingAway] = useState(false);
	const [imageValues, setImageValues] = useState<FormValues>();
	const [isImage1Loading, setIsImage1Loading] = useState(false);
	const [isImage2Loading, setIsImage2Loading] = useState(false);
	const [isVideo1Loading, setIsVideo1Loading] = useState(false);
	const [isVideo2Loading, setIsVideo2Loading] = useState(false);
	const [confirmResult, setConfirmResult] = useState<confirmResultType>({
		loading: false,
		result: false,
	});
	const isSubmitBlocked = isImage1Loading || isImage2Loading || isVideo1Loading || isVideo2Loading;
	const pageForm = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});
	const manuallySave = useRef(false);
	const { control, handleSubmit, watch, trigger, reset, setError, formState, setFocus, getValues } = pageForm;
	const screenshot1FormValue = watch('screenshot1');
	const screenshot2FormValue = watch('screenshot2');
	const video1FormValue = watch('video1');
	const video1ImageFormValue = watch('video1Image');
	const video2FormValue = watch('video2');
	const video2ImageFormValue = watch('video2Image');

	useLayoutEffect(() => {
		const loadProductDetails = async () => {
			// setIsLoading(true);
			const result = await dispatch(getProjectProduct(parseInt(projectId)));
			// setIsLoading(false);

			if (!getProjectProduct.fulfilled.match(result)) {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: result.error.message || t('api.defaultError'),
				});
				return;
			} else if (result.payload) {
				setProductDetails(result.payload);
				const galleryImages = convertGalleryToPlain(result.payload.galleryItems);
				setImageValues(galleryImages);
				const values = {
					...galleryImages,
				};
				reset(values);
			}
		};

		loadProductDetails();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, projectId]);

	const convertGalleryToPlain = (galleryItems: ProductGalleryItemDto[] | undefined): FormValues => {
		if (galleryItems == null)
			return {
				landscapeImage: null,
				coverImage: null,
				heroImage: null,
				screenshot1: null,
				screenshot2: null,
				screenshot3: null,
				screenshot4: null,
				screenshot5: null,
				screenshot6: null,
				video1: [],
				video1Image: null,
				video2: [],
				video2Image: null,
			};

		const coverLandscapeImage = getGalleryItems(galleryItems, GalleryItemType.Landscape, GalleryItemGroup.CoverAsset)?.[0];
		const coverSquareImage = getGalleryItems(galleryItems, GalleryItemType.Square, GalleryItemGroup.CoverAsset)?.[0];
		const heroImage = getGalleryItems(galleryItems, GalleryItemType.HeroImage, GalleryItemGroup.Hero)?.[0];
		const screenshots = getGalleryItems(galleryItems, GalleryItemType.ScreenShot, GalleryItemGroup.ScreenShot);
		const videos = getGalleryItems(galleryItems, GalleryItemType.Trailer, GalleryItemGroup.Video);

		const screenshotImages = Array.from(Array(min([SCREENSHOTS_LENGTH, screenshots.length])), (_, index) => index)
			.map((index) => ({
				fileName: screenshots[index].fileName as string,
				s3Key: screenshots[index].s3Key as string,
				imageSrc: screenshots[index].url,
			}))
			.reduce((a, v, i) => ({ ...a, [`screenshot${i + 1}`]: v }), {});

		const restImages = {
			landscapeImage: coverLandscapeImage
				? {
						fileName: coverLandscapeImage.fileName as string,
						s3Key: coverLandscapeImage.s3Key as string,
						imageSrc: coverLandscapeImage.url,
					}
				: null,
			coverImage: coverSquareImage
				? {
						fileName: coverSquareImage.fileName as string,
						s3Key: coverSquareImage.s3Key as string,
						imageSrc: coverSquareImage.url,
					}
				: null,
			heroImage: heroImage
				? {
						fileName: heroImage.fileName as string,
						s3Key: heroImage.s3Key as string,
						imageSrc: heroImage.url,
					}
				: null,
			video1: !!videos?.length
				? [
						{
							fileName: videos[0].fileName as string,
							s3Key: videos[0].s3Key as string,
						},
					]
				: [],
			video1Image:
				!!videos?.length && !!videos[0].childItems?.length
					? {
							fileName: videos[0].childItems?.[0]?.fileName as string,
							s3Key: videos[0].childItems?.[0]?.s3Key as string,
							imageSrc: videos[0].childItems?.[0]?.url,
						}
					: null,
			video2:
				videos?.length >= 2
					? [
							{
								fileName: videos[1].fileName as string,
								s3Key: videos[1].s3Key as string,
							},
						]
					: [],
			video2Image:
				videos?.length >= 2 && !!videos[1].childItems?.length
					? {
							fileName: videos[1].childItems?.[0]?.fileName as string,
							s3Key: videos[1].childItems?.[0]?.s3Key as string,
							imageSrc: videos[1].childItems?.[0]?.url,
						}
					: null,
		};

		return { ...restImages, ...screenshotImages } as FormValues;
	};

	const preValidateForm = async () => {
		// const image1WithoutVideo = video1ImageFormValue && !video1FormValue?.length;
		// const video1WithoutImage = !video1ImageFormValue && video1FormValue?.length;
		const image2WithoutVideo = video2ImageFormValue && !video2FormValue?.length;
		const video2WithoutImage = !video2ImageFormValue && video2FormValue?.length;

		if (
			// image1WithoutVideo ||
			// video1WithoutImage ||
			image2WithoutVideo ||
			video2WithoutImage
		) {
			await trigger(undefined);

			// if (image1WithoutVideo) {
			// 	setError('video1', {
			// 		message: t('validation.required', {
			// 			fieldName: t('addProductPage.Video1'),
			// 		}),
			// 	});
			// }

			// if (video1WithoutImage) {
			// 	setError('video1Image', {
			// 		message: t('validation.required', {
			// 			fieldName: t('addProductPage.Video1image'),
			// 		}),
			// 	});
			// }

			if (image2WithoutVideo) {
				setError('video2', {
					message: t('validation.required', {
						fieldName: t('addProductPage.Video2'),
					}),
				});
			}

			if (video2WithoutImage) {
				setError('video2Image', {
					message: t('validation.required', {
						fieldName: t('addProductPage.Video2image'),
					}),
				});
			}

			focusFirstErrorField({
				setFocus,
				errors: formState.errors,
				fields: Object.keys(validationSchema.fields),
			});

			return false;
		}

		return true;
	};

	const onSubmit = async (values: FormValues) => {
		if (isSubmitBlocked) return;

		const data: UpdateProjectStoreDetailsCommand = {
			galleryItems: [
				{
					type: GalleryItemType.Square,
					group: GalleryItemGroup.CoverAsset,
					s3Key: values.coverImage?.s3Key,
					fileName: values.coverImage?.fileName,
				},
				{
					type: GalleryItemType.Landscape,
					group: GalleryItemGroup.CoverAsset,
					s3Key: values.landscapeImage?.s3Key,
					fileName: values.landscapeImage?.fileName,
				},
				{
					type: GalleryItemType.HeroImage,
					group: GalleryItemGroup.Hero,
					s3Key: values.heroImage?.s3Key,
					fileName: values.heroImage?.fileName,
				},
			],
		};

		Array.from(Array(SCREENSHOTS_LENGTH), (_, index) => index).forEach((index) => {
			const screenshotKey = `screenshot${index + 1}` as keyof FormValues;
			const screenshot = values[screenshotKey];
			if (screenshot) {
				data.galleryItems?.push({
					type: GalleryItemType.ScreenShot,
					group: GalleryItemGroup.ScreenShot,
					s3Key: (screenshot as ImageFileModel).s3Key,
					fileName: (screenshot as ImageFileModel).fileName,
				});
			}
		});

		if (values.video1Image) {
			data.galleryItems?.push({
				type: GalleryItemType.Trailer,
				group: GalleryItemGroup.Video,
				s3Key: values.video1[0].s3Key,
				fileName: values.video1[0].fileName,
				childItems: [
					{
						type: GalleryItemType.VideoImage,
						group: GalleryItemGroup.ScreenShot,
						s3Key: values.video1Image?.s3Key,
						fileName: values.video1Image?.fileName,
					},
				],
			});
		}

		if (values.video2Image) {
			data.galleryItems?.push({
				type: GalleryItemType.Trailer,
				group: GalleryItemGroup.Video,
				s3Key: values.video2[0].s3Key,
				fileName: values.video2[0].fileName,
				childItems: [
					{
						type: GalleryItemType.VideoImage,
						group: GalleryItemGroup.ScreenShot,
						s3Key: values.video2Image?.s3Key,
						fileName: values.video2Image?.fileName,
					},
				],
			});
		}

		if (!manuallySave.current) {
			setConfirmResult({
				loading: true,
				result: false,
			});
		}

		setIsLoading(true);

		const result = await dispatch(updateProjectStoreDetails({ projectId: parseInt(projectId), ...data }));

		setIsLoading(false);

		if (updateProjectStoreDetails.fulfilled.match(result)) {
			reset({ ...convertGalleryToPlain(data.galleryItems) }, { keepDirty: false });
			OmniNotification.success({
				message: t('addProductPage.ProjectDetails.success.message', {
					type: t('addProductPage.ProjectAssets'),
				}),
				description: t('addProductPage.ProjectDetails.success.description', {
					projectName: productDetails?.name,
					type: t('addProductPage.ProjectAssets'),
				}),
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: true,
				});
			}
		} else {
			OmniNotification.error({
				message: t('notification.error.title'),
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: false,
				});
			}
		}
	};

	const handleFinish = async () => {
		const isValid = await preValidateForm();
		manuallySave.current = true;
		isValid && (await handleSubmit(onSubmit)());
	};

	const hasVideo = () => {
		return imageValues?.video1?.length || imageValues?.video2?.length;
	};

	const beforeUploadVideoValidation = async (file: File) => {
		const formatVailid = isFileFormatValid(file, VIDEO_ALLOWED_FORMATS);
		if (!formatVailid) {
			return {
				success: false,
				errorMessage: t('uploader.fileTypeError'),
			};
		}
		const valid = await isVideoExactResolutionValid(file, VIDEO_EXACT_RESOLUTION);
		if (valid) {
			return { success: true };
		} else {
			return {
				success: false,
				errorMessage: t('uploader.videoExactResolutionError', {
					...VIDEO_EXACT_RESOLUTION,
				}),
			};
		}
	};

	useEffect(() => {
		if (shouldCallSubmitWhenNavigatingAway) {
			manuallySave.current = false;
			handleSubmit(onSubmit)();
		}
	}, [shouldCallSubmitWhenNavigatingAway]);

	if (!productDetails) return <></>;

	return (
		<>
			<RouteLeavingGuard
				when={!isEmpty(formState.dirtyFields)}
				confirmResult={confirmResult!}
				confirmHandler={() => {
					setShouldCallSubmitWhenNavigatingAway(true);
				}}
				isFormValid={formState.isValid}
			/>
			<FormProvider {...pageForm}>
				<Form data-testid="form" layout="vertical" onFinish={handleFinish} onFinishFailed={preValidateForm}>
					<div className="scroll-box">
						<div className="form-label">{t('addProductPage.coverAssets')}</div>
						<Row gutter={[20, 48]}>
							<Col span={24}>
								<div
									style={{
										display: 'flex',
										flexFlow: 'row wrap',
										marginBottom: '24px',
									}}
								>
									<div
										style={{
											display: 'block',
											flexGrow: 0,
											flexShrink: 0,
											flexBasis: '64%',
											maxWidth: '64%',
										}}
									>
										<div
											style={{
												paddingRight: '2%',
											}}
											className="uploader uploader--md aspect16to9"
										>
											<Controller
												name="landscapeImage"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														projectName={productDetails.name}
														defaultFileList={generateUploadFileList(imageValues?.landscapeImage)}
														defaultImageUrl={imageValues?.landscapeImage?.imageSrc}
														field={field as any}
														exactResolution={{
															width: 640,
															height: 360,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">{t('addProductPage.landscapeImage.hint1')}</div>
											<div className="uploader__hint">{t('addProductPage.landscapeImage.hint2')}</div>
										</div>
									</div>
									<div
										style={{
											display: 'block',
											flexGrow: 0,
											flexShrink: 0,
											flexBasis: '36%',
											maxWidth: '36%',
										}}
									>
										<div className="uploader uploader--sm">
											<Controller
												name="coverImage"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														projectName={productDetails.name}
														defaultFileList={generateUploadFileList(imageValues?.coverImage)}
														defaultImageUrl={imageValues?.coverImage?.imageSrc}
														field={field as any}
														exactResolution={{
															width: 512,
															height: 512,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">{t('addProductPage.coverImage.hint1')}</div>
											<div className="uploader__hint">
												<div>{t('addProductPage.coverImage.hint2')}</div>
											</div>
										</div>
									</div>
								</div>
								<Row gutter={[24, 24]}>
									<Col span={24}>
										<div className="uploader uploader--md">
											<Controller
												name="heroImage"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														projectName={productDetails.name}
														defaultFileList={generateUploadFileList(imageValues?.heroImage)}
														defaultImageUrl={imageValues?.heroImage?.imageSrc}
														field={field as any}
														exactResolution={{
															width: 1200,
															height: 360,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">{t('addProductPage.heroImage.hint1')}</div>
											<div className="uploader__hint">{t('addProductPage.heroImage.hint2')}</div>
										</div>
									</Col>
								</Row>
							</Col>
							<Col span={24}>
								<div className="form-label">{t('addProductPage.screenshots')}</div>
								<Row gutter={[24, 24]}>
									{Array.from(Array(SCREENSHOTS_LENGTH), (_, index) => index).map((index) => {
										const name = `screenshot${index + 1}`;
										return (
											<Col span={8} key={index} style={{ marginTop: '8px', marginBottom: '8px' }}>
												<div className="uploader uploader--md aspect16to9">
													<Controller
														name={name as Path<FormValues>}
														control={control}
														render={({ field, fieldState }) => (
															<ImageFileUpload
																error={fieldState.error}
																className="uploader__box"
																buttonText={''}
																uploadLinkFunction={GetUploadLink}
																projectName={productDetails.name}
																onImageChange={() => {
																	if (field.name === 'screenshot1' || field.name === 'screenshot2') {
																		return;
																	}
																	trigger('screenshot1');
																	trigger('screenshot2');
																}}
																defaultFileList={generateUploadFileList((imageValues as any)?.[name])}
																defaultImageUrl={(imageValues as any)?.[name]?.imageSrc}
																field={field as any}
																exactResolution={{
																	width: 1280,
																	height: 720,
																}}
															/>
														)}
													/>
													<div className="uploader__caption">
														{t('addProductPage.screenshots.hint1', {
															screenshotNum: index + 1,
														})}{' '}
														{index + 1 > SCREENSHOTS_REQUIRED_LENGTH && t('addProductPage.videos.optional')}
													</div>
													<div className="uploader__hint">{t('addProductPage.screenshots.hint2')}</div>
												</div>
											</Col>
										);
									})}
								</Row>
							</Col>
							<Col span={24}>
								<div className="form-label">
									{t('addProductPage.videos')} {/* {t('addProductPage.videos.optional')} */}
								</div>
								<Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
									<Col span={6}>
										<div className="uploader__caption">
											{t('addProductPage.videos.hint1', {
												screenshotNum: 1,
											})}{' '}
											{/* {t('addProductPage.videos.optional')} */}
										</div>
										<div className="uploader__hint">{t('addProductPage.videos.hint2')}</div>
									</Col>
									<Col span={6}>
										<Controller
											name="video1"
											control={control}
											render={({ field, fieldState }) => (
												<FileUpload
													defaultFileList={generateUploadFileList(imageValues?.video1)}
													error={fieldState.error}
													uploadLinkFunction={GetUploadLink}
													field={field as any}
													projectName={productDetails.name}
													acceptedFileFormat={VIDEO_ALLOWED_FORMATS}
													acceptedFileSize={100}
													buttonText={t('addProductPage.videos.uploadVideoFile')}
													beforeUploadValidation={beforeUploadVideoValidation}
													onLoadingChange={setIsVideo1Loading}
													onFileListChange={(files) => {
														if (!files.length) {
															setImageValues({
																...imageValues,
																video1: [],
															} as FormValues);
														}
														trigger('video1Image');
													}}
													className="uploader__btn"
												/>
											)}
										/>
									</Col>
									<Col span={6}>
										<div className="uploader__caption">
											{t('addProductPage.videos.hint1', {
												screenshotNum: 2,
											})}{' '}
											{t('addProductPage.videos.optional')}
										</div>
										<div className="uploader__hint">{t('addProductPage.videos.hint2')}</div>
									</Col>
									<Col span={6}>
										<Controller
											name="video2"
											control={control}
											render={({ field, fieldState }) => (
												<FileUpload
													defaultFileList={generateUploadFileList(imageValues?.video2)}
													error={fieldState.error}
													uploadLinkFunction={GetUploadLink}
													field={field as any}
													acceptedFileFormat={VIDEO_ALLOWED_FORMATS}
													acceptedFileSize={100}
													projectName={productDetails.name}
													buttonText={t('addProductPage.videos.uploadVideoFile')}
													beforeUploadValidation={beforeUploadVideoValidation}
													onLoadingChange={setIsVideo2Loading}
													onFileListChange={(files) => {
														if (!files.length) {
															setImageValues({
																...imageValues,
																video2: [],
															} as FormValues);
														}
														trigger('video2Image');
													}}
													className="uploader__btn"
												/>
											)}
										/>
									</Col>
								</Row>
								<Row gutter={[24, 24]} style={{ marginTop: '8px', marginBottom: '8px' }}>
									<Col span={12}>
										<div className="uploader uploader--md aspect16to9">
											<Controller
												name="video1Image"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														projectName={productDetails.name}
														defaultFileList={generateUploadFileList(imageValues?.video1Image)}
														defaultImageUrl={imageValues?.video1Image?.imageSrc}
														onLoadingChange={setIsImage1Loading}
														field={field as any}
														onImageChange={() => {
															trigger('video1');
														}}
														exactResolution={{
															width: 1280,
															height: 720,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">
												{t('addProductPage.videos.trailer.hint1', {
													screenshotNum: 1,
												})}
											</div>
											<div className="uploader__hint">{t('addProductPage.videos.trailer.hint2')}</div>
										</div>
									</Col>
									<Col span={12}>
										<div className="uploader uploader--md aspect16to9">
											<Controller
												name="video2Image"
												control={control}
												render={({ field, fieldState }) => (
													<ImageFileUpload
														error={fieldState.error}
														buttonText={''}
														className="uploader__box"
														uploadLinkFunction={GetUploadLink}
														projectName={productDetails.name}
														defaultFileList={generateUploadFileList(imageValues?.video2Image)}
														defaultImageUrl={imageValues?.video2Image?.imageSrc}
														onLoadingChange={setIsImage2Loading}
														field={field as any}
														onImageChange={() => {
															trigger('video2');
														}}
														exactResolution={{
															width: 1280,
															height: 720,
														}}
													/>
												)}
											/>
											<div className="uploader__caption">
												{t('addProductPage.videos.trailer.hint1', {
													screenshotNum: 2,
												})}
											</div>
											<div className="uploader__hint">{t('addProductPage.videos.trailer.hint2')}</div>
										</div>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>
					<Button data-testid="submit-btn" loading={isLoading} type="primary" htmlType="submit" size="large">
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
}
